import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.service';

const FeatureFlagContext = createContext();

export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

// Fallback features when API fails
const FALLBACK_FEATURES = {
  EQUIPMENT_CATEGORIES: { enabled: false },
  ENHANCED_SEARCH: { enabled: false },
  ADVANCED_REPORTING: { enabled: false },
  JOB_EQUIPMENT_ASSIGNMENT: { enabled: false },
  EQUIPMENT_PLANNING_MODULE: { enabled: false },
  QUICK_STACK_SCANNING: { enabled: false },
  SMART_EQUIPMENT_ASSIGNMENT: { enabled: true }
};

// Helper functions for localStorage persistence
const STORAGE_KEY = 'bavs_feature_flags';

const loadCachedFeatures = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      console.log('Loaded cached feature flags:', parsed);
      return parsed;
    }
  } catch (err) {
    console.warn('Failed to load cached feature flags:', err);
  }
  return FALLBACK_FEATURES;
};

const saveFeaturesCache = (features) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
    console.log('Saved feature flags to cache');
  } catch (err) {
    console.warn('Failed to save feature flags to cache:', err);
  }
};

export function FeatureFlagProvider({ children }) {
  const [features, setFeatures] = useState(() => loadCachedFeatures());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);

  // Fetch feature flags from backend with caching
  const fetchFeatureFlags = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // Don't refetch if we fetched within the last 10 seconds (unless forced)
    if (!forceRefresh && now - lastFetch < 10000) {
      return;
    }
    
    try {
      const response = await api.get('/feature-flags/features');
      if (response.data?.success) {
        const newFeatures = response.data.data || response.data.features;
        setFeatures(newFeatures);
        saveFeaturesCache(newFeatures);
        setLastFetch(now);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      setError('Failed to load feature configuration');
      
      // Only use fallback features if we don't already have features loaded
      // This prevents auth race conditions from resetting enabled flags
      setFeatures(prev => {
        const hasExistingFeatures = Object.keys(prev).length > 0 && 
          Object.values(prev).some(feature => feature.enabled);
        
        if (hasExistingFeatures) {
          console.warn('Keeping existing feature flags due to API error');
          return prev; // Keep existing features
        } else {
          console.warn('Using fallback features due to initial load failure');
          return FALLBACK_FEATURES; // Use fallback only on initial load
        }
      });
    } finally {
      setLoading(false);
    }
  }, [lastFetch]);

  // Toggle a feature flag
  const toggleFeature = useCallback(async (featureName, enabled) => {
    try {
      const response = await api.post('/feature-flags/admin/toggle-feature', {
        featureName,
        enabled
      });
      
      if (response.status === 200 && response.data?.name) {
        // Update local state immediately
        const updatedFeatures = {
          ...features,
          [featureName]: {
            ...features[featureName],
            enabled
          }
        };
        setFeatures(updatedFeatures);
        saveFeaturesCache(updatedFeatures);
        
        // Refetch all flags to ensure consistency (force refresh to bypass cache)
        await fetchFeatureFlags(true);
        
        return { success: true };
      } else {
        console.error('Toggle failed - unexpected response:', response.data);
        return { 
          success: false, 
          error: response.data?.error || 'Server returned unexpected response' 
        };
      }
    } catch (err) {
      console.error('Error toggling feature:', err);
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to toggle feature' 
      };
    }
  }, [fetchFeatureFlags]);

  // Check if a feature is enabled
  const isFeatureEnabled = useCallback((featureName) => {
    return features[featureName]?.enabled || false;
  }, [features]);

  // Load flags on mount
  useEffect(() => {
    fetchFeatureFlags(true); // Force initial fetch
  }, [fetchFeatureFlags]);

  // Poll for updates every 30 seconds to catch changes from other users (reduced frequency)
  useEffect(() => {
    const interval = setInterval(() => fetchFeatureFlags(false), 30000);
    return () => clearInterval(interval);
  }, [fetchFeatureFlags]);

  const value = {
    features,
    loading,
    error,
    isFeatureEnabled,
    toggleFeature,
    refreshFlags: () => fetchFeatureFlags(true)
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}